import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Loader2, X } from "lucide-react";
import { imgUrl } from "@/components/landing/useProducts";
import { compressImage } from "@/lib/compressImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ImageUpload = ({ headers, images, onChange, multiple = false, testId = "image-upload" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const list = Array.isArray(images) ? images : images ? [images] : [];

  const upload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", await compressImage(f));
        const r = await axios.post(`${API}/admin/upload`, fd, { headers, timeout: 120000 });
        urls.push(r.data.url);
      }
      onChange(multiple ? [...list, ...urls] : urls[0]);
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : err?.response?.status === 413 ? "Image too large (max 25MB)." : err?.code === "ECONNABORTED" ? "Upload timed out — check your connection and try again." : "Upload failed. Try a JPG or PNG photo.");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const remove = (i) => onChange(multiple ? list.filter((_, idx) => idx !== i) : "");

  return (
    <div data-testid={testId} className="flex flex-wrap gap-2">
      {list.map((src, i) => (
        <div key={src + i} className="relative h-20 w-16 rounded-lg overflow-hidden border border-[#e3e2dc] bg-[#f3f2ec]">
          <img src={imgUrl(src)} alt="" className="h-full w-full object-cover" />
          <button type="button" data-testid={`${testId}-remove-${i}`} onClick={() => remove(i)} aria-label="Remove image"
            className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-[#0a0a0b]/80 text-white flex items-center justify-center hover:bg-[#ff2a85]">
            <X size={11} />
          </button>
        </div>
      ))}
      {(multiple || list.length === 0) && (
        <button type="button" data-testid={`${testId}-button`} onClick={() => ref.current?.click()} disabled={busy}
          className="h-20 w-16 rounded-lg border border-dashed border-[#5f5e5a]/50 hover:border-[#ff2a85] text-[#5f5e5a] hover:text-[#ff2a85] flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
          <span className="font-mono-d text-[8px] uppercase tracking-[0.15em]">{busy ? "…" : "Add"}</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/*,.heic,.heif" multiple={multiple} className="hidden" data-testid={`${testId}-input`}
        onChange={(e) => upload(Array.from(e.target.files || []))} />
    </div>
  );
};
